import type { EvmChainKey } from '@infinex/chain-config';
import {
  type ExternalSigningDeviceType,
  type MultiSigType,
  type UserWalletNested,
  type VaultsDatasetVault,
  VAULTS_LABEL,
  type LoadingState,
  type VaultsLabel,
} from '@infinex/types';
import { useDataset } from '@infinex/user';

import { useWalletsV2Nested } from './useWalletsV2Nested';

// TODO multi-wallet: Rename everything in this file from singular custody naming to group-based naming.
// - e.g. useCustodyGroupsList, RecoveryPhraseGroup, PrivateKeyGroup, CustodyGroup, etc.
// - This file's current types/hooks use "Custody" and "ForList" in a singular form, but the real model
//   is grouped! These should be renamed when this file's structure is next touched.
// - See: the true meaning is "groups" of wallets, and our UI/logic want to work in those groupings.
// (we didn't fully understand this when we were first implementing)
// This should probably just return a superset of the `useWalletsV2Nested` return type (add display properties as needed, plus vault)
// Mistakes were made
export type RecoveryPhraseForList = {
  groupKind: 'recoveryPhrase';
  recoveryPhraseId: string;
  isImported: boolean;
  wallets: WalletForList[];
};

export type PrivateKeyForList = {
  groupKind: 'privateKey';
  wallet: WalletForList;
};

export type ExternalSigningDeviceForList = {
  groupKind: 'externalSigningDevice';
  externalSigningDeviceId: string;
  deviceType: ExternalSigningDeviceType;
  label: string;
  wallets: ExternalWalletForList[];
};

export type WalletForList = UserWalletNested<{
  custody: 'wallet';
}>;

export type ExternalWalletForList = UserWalletNested<{
  custody: 'external-signer';
}>;

export type MultiSigWalletForList = UserWalletNested<{
  custody: 'multi-sig';
}> & {
  groupKind: 'multiSigWallet';
  multiSigWalletId: string;
  multiSigType: MultiSigType;
  address: string;
  /** Chains with confirmed-live Safe deployments (dataset SoT). */
  deployedChains: EvmChainKey[];
};

export type VaultForList = {
  groupKind: 'vault';
  id: 'vault';
  custody: 'vault';
  label: VaultsLabel;
  vaults: VaultsDatasetVault[];
};

export type CustodyForList =
  | RecoveryPhraseForList
  | PrivateKeyForList
  | ExternalSigningDeviceForList
  | MultiSigWalletForList
  | VaultForList;

/**
 * Returns list of custodies (wallets + vaults) with recovery phrase grouping for wallets because we need to show them grouped
 * Design/UI-driven - Used for displaying custody lists in the UI (portfolio, move dialog, etc.)
 *
 * See `useCustodiesWithBalance` for custodies **with** USD balances. Please keep changes in sync with both
 *
 * @param options.excludeVaults - If true, only returns wallets (useful for e.g. swidge where vaults are invalid)
 * @returns Array of custodies
 */
export function useCustodiesList(options: {
  excludeVaults: boolean;
}): LoadingState<CustodyForList[]> {
  const { data: walletsV2Nested, status: walletsV2Status } =
    useWalletsV2Nested();
  const { data: vaults, status: vaultsStatus } = useDataset('vaults');

  if (walletsV2Status !== 'loaded' || vaultsStatus !== 'loaded') {
    return {
      isLoading: true,
      value: undefined,
    };
  }

  const phrasesWithWallets: RecoveryPhraseForList[] = Object.values(
    walletsV2Nested.seedPhrases
  )
    .map((recoveryPhrase) => {
      const walletsForList = Object.values(recoveryPhrase.wallets).map(
        (wallet) => {
          return {
            ...wallet,
            custody: 'wallet' as const,
          };
        }
      );

      return {
        ...recoveryPhrase,
        groupKind: 'recoveryPhrase' as const,
        recoveryPhraseId: recoveryPhrase.id,
        isImported: recoveryPhrase.isImported,
        wallets: walletsForList,
      };
    })
    // Filter out seed phrases with no wallets
    .filter((phrase) => phrase.wallets.length > 0);

  const privateKeysWithWallets: PrivateKeyForList[] = Object.values(
    walletsV2Nested.privateKeys
  ).map((privateKey) => {
    return {
      groupKind: 'privateKey',
      wallet: {
        ...privateKey.wallet,
        custody: 'wallet' as const,
      },
    };
  });

  const externalSigningDevicesWithWallets: ExternalSigningDeviceForList[] =
    Object.values(walletsV2Nested.externalSigningDevices || {})
      .map((device) => {
        const walletsForList = Object.values(device.wallets).map((wallet) => ({
          ...wallet,
          custody: 'external-signer' as const,
        }));

        return {
          groupKind: 'externalSigningDevice' as const,
          externalSigningDeviceId: device.id,
          deviceType: device.deviceType,
          label: device.label,
          wallets: walletsForList,
        };
      })
      // Filter out devices with no wallets
      .filter((device) => device.wallets.length > 0);

  const multiSigWalletsForList: MultiSigWalletForList[] = Object.values(
    walletsV2Nested.multiSigWallets || {}
  ).map((msw) => ({
    ...msw,
    groupKind: 'multiSigWallet' as const,
    custody: 'multi-sig' as const,
  }));

  // Determine if we should show vaults in the list
  // Only show if user has vaults configured OR can configure them
  const isVaultable =
    vaults.hasAnyVaultsConfigured || vaults.canConfigureVaults;

  // Extract vault calculation to its own variable for clarity
  const vaultsForList: VaultForList[] =
    isVaultable && !options.excludeVaults
      ? [
          {
            groupKind: 'vault',
            id: 'vault',
            label: VAULTS_LABEL,
            custody: 'vault' as const,
            vaults: vaults.vaults,
          },
        ]
      : [];

  return {
    isLoading: false,
    value: [
      ...phrasesWithWallets,
      ...privateKeysWithWallets,
      ...externalSigningDevicesWithWallets,
      ...multiSigWalletsForList,
      ...vaultsForList,
    ],
  };
}
